export const networkStats = [
  { value: "99.99%", label: "Platform uptime SLA" },
  { value: "700+", label: "Direct interconnects" },
  { value: "4", label: "Core PoPs worldwide" },
  { value: "<45ms", label: "Median PDD, US traffic" },
  { value: "24/7/365", label: "Human NOC coverage" },
  { value: "1000+", label: "Termination destinations" },
];

export const architecturePoPs = [
  {
    region: "North America West",
    city: "Las Vegas, US",
    carrierHotels: "Switch SUPERNAP · Coresite LA",
    transits: ["Lumen (Level 3)", "Cogent", "Zayo", "Telia"],
    peering: "Any2 LA, Equinix LA",
    protocols: "SIP, TLS, SRTP, RTP, WebRTC",
    codecs: "G.711, G.729, Opus, G.722",
    color: "accent",
  },
  {
    region: "Europe Central",
    city: "Frankfurt, DE",
    carrierHotels: "",
    transits: ["Deutsche Telekom", "Arelion (Telia)", "Cogent", "Zayo"],
    peering: "DE-CIX, KleyReX",
    protocols: "SIP, TLS, SRTP, RTP, WebRTC",
    codecs: "G.711, G.729, Opus, G.722, iLBC",
    color: "accent",
  },
  {
    region: "Europe East",
    city: "Prague, CZ",
    carrierHotels: "CE Colo · TTC Teleport · Nagano Datacenter",
    transits: ["Deutsche Telekom", "Arelion (Telia)", "Cogent", "GTT"],
    peering: "NIX.CZ, Peering.cz",
    protocols: "SIP, TLS, SRTP, RTP, WebRTC",
    codecs: "G.711, G.729, Opus",
    color: "secondary",
  },
  {
    region: "Asia Pacific",
    city: "Singapore, SG",
    carrierHotels: "Equinix SG · Global Switch",
    transits: ["Singtel", "Telstra", "NTT", "PCCW"],
    peering: "SGIX, Equinix SG",
    protocols: "SIP, TLS, SRTP, RTP, WebRTC",
    codecs: "G.711, G.729, Opus, G.722",
    color: "primary",
  },
];

export const routingFeatures = [
  {
    icon: "ri-arrow-left-right-line",
    title: "Multi-tier LCR engine",
    desc: "Every destination maps to a preferred (Premium) route and up to two fallback (Standard) routes. The LCR engine ranks paths by ASR, PDD, and per-minute cost in real time, placing calls on the best-performing route available at the moment of dialing.",
  },
  {
    icon: "ri-shuffle-line",
    title: "Automatic failover",
    desc: "If a route's ASR drops below its configured threshold or PDD exceeds the ceiling, traffic shifts to the next-best route within one call attempt — no manual intervention, no dropped sessions. The degraded route is suppressed until its KPIs recover.",
  },
  {
    icon: "ri-stack-line",
    title: "N+1 SBC redundancy",
    desc: "Every PoP runs at least one hot-standby SBC. Registration state, active calls, and routing tables synchronize continuously. If a primary SBC faults, the standby assumes its IP, picks up registrations, and starts handling calls with zero human latency.",
  },
  {
    icon: "ri-global-line",
    title: "Multi-transit per PoP",
    desc: "Each point of presence peers with three or more transit providers using diverse physical fiber paths and separate BGP sessions. No single transit failure can isolate a PoP. Capacity is over-provisioned at 40% headroom by design.",
  },
  {
    icon: "ri-refresh-line",
    title: "Continuous route testing",
    desc: "Probes run every 60 seconds from each PoP to every destination prefix. A probe places a test call, measures ASR and PDD, and feeds results into the LCR engine. Routes that fail three consecutive probes are auto-quarantined until manually reviewed.",
  },
  {
    icon: "ri-radar-line",
    title: "Geographic routing",
    desc: "Ingress traffic is steered to the nearest PoP by latency. Egress traffic selects the PoP with the lowest measured PDD to the destination. For regulatory destinations that mandate in-country media relay, traffic is forced through the local PoP.",
  },
];

export const protocols = [
  { name: "SIP", version: "2.0", transport: "UDP / TCP", category: "Signaling", status: "Full support" },
  { name: "SIP-over-TLS", version: "1.3", transport: "TCP", category: "Signaling", status: "Full support" },
  { name: "WebRTC", version: "—", transport: "TCP / UDP", category: "Signaling + Media", status: "Full support" },
  { name: "IAX2", version: "2", transport: "UDP", category: "Signaling + Media", status: "On request" },
  { name: "RTP", version: "—", transport: "UDP", category: "Media", status: "Full support" },
  { name: "SRTP", version: "—", transport: "UDP", category: "Media", status: "Full support" },
  { name: "ZRTP", version: "—", transport: "UDP", category: "Media", status: "On request" },
];

export const codecs = [
  { name: "G.711", type: "Narrowband", bitrate: "64 kbps", mos: "4.4", status: "Full support" },
  { name: "G.729", type: "Narrowband", bitrate: "8 kbps", mos: "3.92", status: "Full support" },
  { name: "G.722", type: "Wideband", bitrate: "64 kbps", mos: "4.5", status: "Full support" },
  { name: "Opus", type: "Fullband", bitrate: "6–510 kbps", mos: "4.6", status: "Full support" },
  { name: "iLBC", type: "Narrowband", bitrate: "13.3 / 15.2 kbps", mos: "3.8", status: "Full support" },
  { name: "AMR-WB", type: "Wideband", bitrate: "6.6–23.85 kbps", mos: "4.1", status: "On request" },
  { name: "G.726", type: "Narrowband", bitrate: "16–40 kbps", mos: "3.85", status: "Full support" },
  { name: "Speex", type: "Narrowband / Wideband", bitrate: "2.15–44 kbps", mos: "3.7", status: "Legacy" },
];

export const monitoringTools = [
  {
    icon: "ri-dashboard-line",
    title: "Real-time KPI dashboard",
    desc: "ASR, PDD, ACD, MOS, jitter, and packet loss visualized per destination, per carrier, per PoP. Refresh every 10 seconds. Configurable thresholds trigger Slack, Telegram, or email alerts.",
  },
  {
    icon: "ri-alarm-warning-line",
    title: "Automatic alerting",
    desc: "When a route's ASR drops below its floor or PDD spikes, the incident is opened, the NOC is paged, and traffic auto-fails over. Alert severity tiers: Warning (ASR -5%), Critical (ASR -15%), and Outage (complete loss).",
  },
  {
    icon: "ri-file-chart-line",
    title: "Daily quality reports",
    desc: "Every morning at 06:00 UTC, a quality digest is emailed to your technical contact — yesterday's ASR/PDD/ACD per top-10 destinations, any route events, and actions taken by the NOC.",
  },
  {
    icon: "ri-history-line",
    title: "30-day CDR archive",
    desc: "Full call detail records retained for 30 days, searchable by calling number, called number, destination, or date range. Export to CSV. Extended retention available for compliance.",
  },
];

export const supportTiers = [
  {
    label: "NOC",
    icon: "ri-send-plane-line",
    channels: "Telegram · WhatsApp · Ticket",
    response: "< 5 min",
    hours: "24/7/365",
  },
  {
    label: "Email",
    icon: "ri-mail-line",
    channels: "noc@voxmaster.com",
    response: "< 1 hour",
    hours: "24/7/365",
  },
  {
    label: "Phone",
    icon: "ri-phone-line",
    channels: "Follow-the-sun hotline",
    response: "< 30 seconds",
    hours: "24/7/365",
  },
  {
    label: "Account Manager",
    icon: "ri-user-star-line",
    channels: "Dedicated contact",
    response: "Business hours",
    hours: "Mon–Fri, 9–18 local",
  },
];

export const architectureLayers = [
  { name: "Ingress SBC", desc: "TLS termination, IP authentication, rate limiting, topology hiding", count: "12+" },
  { name: "Core switch", desc: "SIP routing, LCR lookup, CDR generation, billing feed", count: "6" },
  { name: "Egress SBC", desc: "Codec transcoding, topology hiding, media anchoring", count: "18+" },
  { name: "Monitoring", desc: "Probe runner, KPI aggregator, alert manager, quality reporter", count: "6" },
];

export const liveMetrics = [
  { label: "Global ASR", value: "61.4%", trend: "+0.8 pp", period: "Last 30 days", positive: true },
  { label: "Median PDD (global)", value: "54ms", trend: "-3ms", period: "Last 30 days", positive: true },
  { label: "Route availability", value: "99.98%", trend: "→", period: "Last 30 days", positive: true },
  { label: "Post-dial delay (NA)", value: "38ms", trend: "-2ms", period: "Last 30 days", positive: true },
  { label: "Call completion rate", value: "98.2%", trend: "+0.3 pp", period: "Last 30 days", positive: true },
  { label: "Mean MOS score", value: "4.32", trend: "+0.05", period: "Last 30 days", positive: true },
];